import React, {useEffect, useMemo, useState} from 'react'
import {observer} from 'mobx-react'
import {Box, Stack, Text} from '../../../../vanilla'
import {useBasketStore, useGlobalStore} from '../../../../store/hooks/useStore'
import {CalendarTabs} from './calendar-tabs'
import {TimeSlots} from './time-slots'
import LoadingOverlay from '../../../../components/loading-overlay'
import { slotsMatch } from '../../../../utils/booking-utils'
import SlotsErrorMessaging from './error-messaging'
import { StatusMap } from '../../../../types/commerce'

export const BookingSlotsContainer = () => {

  const {deliverySlots, deliverySlotArray, currentDeliverySlotId, isAppliedPostCodeDeliverable, sameDayDeliveryAvailable, status, deliveryPostalCode, deliverySlotStatus} =
  useBasketStore()
  const {getCustomSitePreferenceById} = useGlobalStore()
  const [smallBasketChargeEnabled, setSmallBasketChargeEnabled] = useState<boolean>(false)
  const [selectedTab, setSelectedTab] = React.useState<number | undefined>()
  const [hasError, setHasError] = useState(false)
  const [loading, setLoading] = useState(true)

  /** Handles default selected calendar tab once user already has booked a slot, but slot needs to rebooked  */
  useEffect(() => {
    if (!deliverySlotArray?.length || !currentDeliverySlotId) {
      return
    }

    deliverySlotArray.forEach((deliverySlot, idx) => {
      for (const slot of deliverySlot.slots || []) {
        if (slotsMatch(slot.id,currentDeliverySlotId)) {
          setSelectedTab(idx)
          break
        }
      }
    })
  }, [deliverySlots, currentDeliverySlotId])

  const availableDatesText = useMemo(() => {
    let text = ''
    if (deliverySlotArray?.length) {
      const firstDay = deliverySlotArray[0]
      const lastDay = deliverySlotArray[deliverySlotArray.length - 1]
      text = `${firstDay?.day} ${firstDay?.month} - ${lastDay?.day} ${lastDay?.month}`
    }
    return text
  }, [deliverySlotArray])


  useEffect(() => {
    async function fetchChargeEnabled() {
      try {
        const chargeEnabled =  Boolean(await getCustomSitePreferenceById('SmallBasketChargeEnabled'))
        setSmallBasketChargeEnabled(chargeEnabled)
      } catch (error) {
        console.error('Error fetching small basket charge custom preference:', error)
      }
    }
  
    fetchChargeEnabled()
  }, [])

  const datesAreAvailable = deliverySlots && isAppliedPostCodeDeliverable && availableDatesText.length

  // Check if same day slots are available, if not, select tomorrow
  useEffect(() => {
    if (!sameDayDeliveryAvailable) {
      setSelectedTab(1)
    }
  }, [sameDayDeliveryAvailable])

  useEffect(() => {
      const requestIsPending = (deliverySlotStatus !== StatusMap.DONE && deliverySlotStatus !== StatusMap.ERROR) || (status === StatusMap.INIT || status === StatusMap.PENDING)
      setHasError(!requestIsPending && deliverySlotStatus === StatusMap.ERROR)
      // Show loading overlay until all data is fetched
      setLoading(requestIsPending)
  }, [deliverySlots, status, deliverySlotStatus])

  // If there is an error fetching delivery slots, show error message
  if (hasError) {
    return <SlotsErrorMessaging error={hasError} />
  }

  // Do not render slots section if postcode is not set
  if (!loading && !deliveryPostalCode && !deliverySlots) {
    return <Stack display="none"></Stack>
  }

  return (
    <Box gridColumn="span 12" paddingBottom="12px" position="relative" minHeight={"450px"}>
      <LoadingOverlay containerStyles={{backgroundColor: 'white'}} isLoading={loading} text={"Loading Delivery Slots..."}/>
      {!loading && (
        <>
          {/* Message to show when dates are/aren't available */}
          {datesAreAvailable ? (
            <Text
              style={{color: '#9b9b9b'}}
              variant="text6"
              marginTop={['8px', '20px']}
              paddingX={['16px', '0px']}
            >
              Available times:{' '}
              <Text
                as="span"
                color="black"
                variant={datesAreAvailable ? 'text7' : 'text3'}
              >
                {availableDatesText}
              </Text>
            </Text>
            ) : null}
    
          {deliverySlots && isAppliedPostCodeDeliverable && deliverySlotArray?.length > 0 ? ( 
            <>
              <CalendarTabs setSelectedTab={setSelectedTab} selectedTab={selectedTab} />
              <TimeSlots selectedTab={selectedTab} smallBasketEnabled={smallBasketChargeEnabled} />
            </>
          ) : 
          // Show error message if there are no delivery slots available
            <SlotsErrorMessaging />
          }
          {smallBasketChargeEnabled ? ( 
            <Stack paddingTop="12px">
              <Text textAlign="center" variant="text6" data-test-selector="minimum-spend">
                Minimum spend of £25 per order.
              </Text>
              <Text textAlign="center" variant="text3" data-test-selector="small-basket-fee-message">
                A small basket fee applies to orders under £40.
              </Text>
              <Text data-test-selector="basket-value-learn-more-link"
                  as="a"
                  href="/customer-support/contact-us"
                  variant={'unstyled'}
                  fontWeight={'medium'}
                  style={{textDecoration: 'underline'}}
                  textAlign="center"
                >
                Learn More
              </Text>
            </Stack>
          ) : null}
        </>
      )}
    </Box>
  )
}

export const BookingSlots = observer(BookingSlotsContainer)
